import { QuoteApproveDialog } from './quoteSlots/QuoteApproveDialog';
import { useState, useEffect, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Download,
  CheckCircle2,
  Clock3,
  CalendarClock,
  FileWarning,
  Check,
  ChevronRight,
  X,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '@/components/atoms/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/dialog';
import { DocumentNotFound } from '@/components/public/DocumentNotFound';
import { SingleScopeActionBar } from '@/components/public/SingleScopeActionBar';
import { SingleScopeHero } from '@/components/public/SingleScopeHero';
import { SingleScopeFooter } from '@/components/public/SingleScopeFooter';
import { SingleScopeLetterhead } from '@/components/public/SingleScopeLetterhead';
import { SingleScopeCompanyRail } from '@/components/public/SingleScopeCompanyRail';
import { SingleScopeReviews } from '@/components/public/SingleScopeReviews';
import { PoweredByNotifi } from '@/components/public/PoweredByNotifi';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import { printableSingleScopeCompany } from '@/lib/quote/singleScopeCompany';
import { offersTwoDepositMethods } from '@/components/organisms/payments/DepositCTA';
import { ServiceFeeNote } from '@/components/organisms/payments/ServiceFeeNote';
import { MembershipOfferBlock } from '@/components/organisms/estimates/MembershipOfferBlock';
import {
  depositPollInterval,
  depositSettlementToken,
  useDepositConfirmOnReturn,
} from '@/lib/quote/depositSettlement';
import PdfViewer from '@/components/documents/estimates/PdfViewer';
import { lineDetailVisibility } from '@/lib/sales/line-detail-visibility';
import { LineItemPhoto } from '@/components/public/LineItemPhoto';
import { isHtmlContent } from '@/lib/utils';
import { RichTextContent } from '@/components/molecules/rich-text-content';
import { RICH_TEXT_CLASS } from '@/lib/rich-text';
import { hasReadableContent } from '@/lib/terms/clientTerms';
import { CustomerTermsBody } from '@/components/public/CustomerTermsBody';
import { singleScopeThemeVars } from '@/lib/quote/singleScopeTheme';
import { publicPost } from '@/lib/api/publicClient';
import { WisetackPublicCta as WisetackEstimateCta } from '@/components/public/WisetackPublicCta';
import { formatRecurringParts } from '@/lib/sales/recurring-format';
import { taxBreakdownDisplayMeta, taxBreakdownDisplayName } from '@/lib/tax/tax-utils';
import { dateInTimeZone } from '@/lib/date-values';
import { resolveEstimateLineAmounts } from '@/lib/sales/line-item-pricing';
import { membershipOfferAdjustmentForLines } from '@/lib/sales/membership-offer';
import {
  QuoteActionProvider,
  useQuoteActions,
  fetchEstimate,
  fmt,
  fmtDate,
  fmtDateOnly,
  type AttachmentImage,
  type EstimateViewProps,
  type PublicEstimateResponse,
} from './quoteActionContext';
import { ItemizedEstimateDocument } from './quoteItemizedDocument';
import { ApproveDeclineSlot } from './quoteSlots/ApproveDeclineSlot';
import { DepositSlot } from './quoteSlots/DepositSlot';
import { OptionalSelectionSlot } from './quoteSlots/OptionalSelectionSlot';
import { paymentPlanRows } from '@/lib/quote/paymentPlanRows';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';
import { usePublicPageScroll } from '@/components/public/usePublicPageScroll';
import { publicEstimateSearch } from '@/lib/sales/public-estimate-search';
import { depositBadgeLabel } from '@/lib/quote/depositCta';

export const Route = createFileRoute('/(public)/estimate/$id')({
  validateSearch: publicEstimateSearch,
  component: QuotePage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Overrides the global `overflow: hidden` on html/body/#root set for the dashboard.
// The document itself must scroll here: the approved-PDF view and the
// SINGLE_SCOPE two-column layout are full-bleed, so they can't hand their
// scrolling to an inner container the way PublicCenteredPage does.
function ScrollableView({ children }: { children: React.ReactNode }) {
  const { embedded } = useQuoteActions();
  usePublicPageScroll(!embedded);
  return <>{children}</>;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function QuotePage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const settlementToken = depositSettlementToken(id, search.deposit);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<PublicEstimateResponse>({
    queryKey: ['public', 'estimate', id, settlementToken],
    queryFn: () => fetchEstimate(id),
    retry: false,
    refetchInterval: (query) => {
      const current = query.state.data as PublicEstimateResponse | undefined;
      return depositPollInterval(
        search.deposit,
        current?.estimate?.depositCollected,
        current?.estimate?.depositClearing,
      );
    },
    refetchOnWindowFocus: search.deposit === 'success',
  });

  // Fast-path on Stripe return: hit the sync confirm endpoint instead of
  // waiting for the webhook (5–30s end-to-end). Idempotent — the webhook still
  // runs as the safety net. Shared with the client portal (only the confirm
  // endpoint differs); fires once per session_id.
  useDepositConfirmOnReturn({
    deposit: search.deposit,
    sessionId: search.session_id,
    depositCollected: data?.estimate?.depositCollected,
    confirm: (sessionId) =>
      publicPost(`/estimates/public/${id}/stripe-deposit-confirm`, { sessionId }),
    onConfirmed: () =>
      void queryClient.invalidateQueries({
        queryKey: ['public', 'estimate', id, settlementToken],
      }),
  });

  if (isLoading) {
    return (
      <PublicCenteredPage className="bg-[#FAFAFA]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </PublicCenteredPage>
    );
  }

  if (isError || !data?.estimate) {
    return <DocumentNotFound message="Estimate not found" />;
  }

  return (
    <EstimateView
      estimate={{
        ...data.estimate,
        depositCollectedSummary:
          data.depositCollectedSummary === undefined
            ? data.estimate.depositCollectedSummary
            : data.depositCollectedSummary,
      }}
      company={data.company}
      customFields={data.customFields ?? []}
      activePdf={data.activePdf ?? null}
      presentation={
        // The resolved DESIGN governs the customer web view: single-scope ⇒
        // SINGLE_SCOPE, any other resolved design ⇒ ITEMIZED. The legacy tenant
        // `presentation` flag is consulted ONLY when no design resolves (document
        // designs off), so a tenant with an itemized default is never stranded on
        // single-scope by a stale flag.
        data.resolvedDocLayout
          ? (data.resolvedDocLayout.branding as { presentation?: string } | undefined)
              ?.presentation === 'SINGLE_SCOPE'
            ? 'SINGLE_SCOPE'
            : 'ITEMIZED'
          : (data.estimateSettings?.presentation ?? 'ITEMIZED')
      }
      presentedByName={data.presentedBy?.name ?? null}
      reviews={data.reviews ?? null}
      resolvedDocLayout={data.resolvedDocLayout ?? null}
      id={id}
      depositSearchState={search.deposit}
      depositSurcharge={data.depositSurcharge}
      payment={data.payment ?? null}
      depositAttempt={data.depositAttempt ?? null}
      serviceFee={data.serviceFee}
      wisetack={data.wisetack ?? null}
      timezone={data.timezone ?? undefined}
    />
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

// Exported for tests: the approved/signed view returns early, so the only way
// to pin what it renders is to mount this directly. EstimateView is a thin
// wrapper that owns the money/action layer through QuoteActionProvider; the
// body reads everything it renders from useQuoteActions().
export function EstimateView(props: EstimateViewProps) {
  return (
    <QuoteActionProvider {...props}>
      <EstimateViewBody />
    </QuoteActionProvider>
  );
}

function EstimateViewBody() {
  const {
    estimate,
    embedded,
    documentCompany,
    customFields,
    v,
    presentation,
    resolvedDocLayout,
    reviews,
    depositSearchState,
    depositSurcharge,
    serviceFee,
    wisetack,
    timezone,
    pdfBaseName,
    serviceAddress,
    customerVisibleTitle,
    estimateDiscountLabel,
    customerNotes,
    attachmentImages,
    paymentPlan,
    setApproveModalOpen,
    declineModalOpen,
    setDeclineModalOpen,
    declineChoice,
    setDeclineChoice,
    declineReason,
    setDeclineReason,
    submitting,
    pdfDisplayUrl,
    requiredRows,
    requiredSubtotal,
    selectedOptionalRows,
    approvalGrandTotal,
    approvalTaxTotal,
    displayMembershipSavings,
    membershipSavingsPlanNames,
    membershipPlanPrice,
    membershipSetupFee,
    selectedDiscountTotal,
    selectedDeposit,
    membershipOffer,
    acceptMembershipOffer,
    setAcceptMembershipOffer,
    selectedMembershipBillingOptionId,
    setSelectedMembershipBillingOptionId,
    subtotalWithoutSetupFee,
    taxBreakdown,
    canAct,
    isExpiredByDate,
    validUntilDate,
    expiredWhileActionable,
    offlineNote,
    terms,
    hasTerms,
    handleDecline,
    effectiveStatus,
    isApprovedPresentation,
    showDecisionFinancing,
    depositCollected,
    depositClearing,
    depositByCheck,
    canPayDeposit,
    approveAndPay,
    depositChargeAmount,
    showServiceFeeNote,
    showApprovedAside,
    showApprovedDeposit,
    isDepositDisputedAfterRedirect,
    isDepositFailedAfterRedirect,
    handleDownloadPdf,
    canApproveOnPage,
  } = useQuoteActions();
  const collectedSummary = estimate.depositCollectedSummary;
  const collectedDiscount = collectedSummary?.discountAmount ?? 0;
  const displayedOptionalRows = selectedOptionalRows.map((row) => {
    if (!acceptMembershipOffer) return row;
    const adjustment = membershipOfferAdjustmentForLines(
      membershipOffer,
      row.lines,
      estimate.lineItems,
    );
    return {
      ...row,
      subtotal: adjustment?.memberSubtotal ?? row.subtotal,
    };
  });

  // The hosted customer page renders the interactive presentations (single-scope
  // or the itemized design document). `resolvedDocLayout` flows through the
  // provider so this screen and the generated/signed PDF read the SAME design.

  // When already approved and PDF is ready, show the signed document directly
  if (isApprovedPresentation && pdfDisplayUrl) {
    return (
      <ScrollableView>
        <div className="min-h-screen bg-[#F6F4F1]">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {depositSearchState === 'success' && (
              <div
                className={`mb-5 rounded-2xl border px-4 py-3 shadow-sm ${
                  depositCollected
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : isDepositDisputedAfterRedirect
                      ? 'border-orange-200 bg-orange-50 text-orange-900'
                      : isDepositFailedAfterRedirect
                        ? 'border-red-200 bg-red-50 text-red-900'
                        : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}
              >
                <div className="flex items-start gap-3">
                  {depositCollected ? (
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                  ) : (
                    <Clock3
                      className={`mt-0.5 h-5 w-5 shrink-0 ${
                        isDepositDisputedAfterRedirect
                          ? 'text-orange-600'
                          : isDepositFailedAfterRedirect
                            ? 'text-red-600'
                            : 'text-amber-600'
                      }`}
                    />
                  )}
                  <div>
                    <p className="font-semibold">
                      {depositCollected
                        ? 'Deposit paid'
                        : isDepositDisputedAfterRedirect
                          ? 'Deposit disputed'
                          : isDepositFailedAfterRedirect
                            ? 'Deposit payment failed'
                            : depositClearing
                              ? estimate.depositClearingRail === 'BANK'
                                ? 'Pending ACH deposit payment'
                                : 'Deposit payment pending'
                              : 'Deposit received, finalizing estimate'}
                    </p>
                    <p className="mt-0.5 text-sm opacity-90">
                      {depositCollected
                        ? collectedSummary
                          ? `${fmt(collectedSummary.chargedAmount)} received.${collectedDiscount > 0 ? ` ${fmt(collectedDiscount)} ACH/check discount; ${fmt(collectedSummary.coveredAmount ?? collectedSummary.depositAmount)} deposit covered.` : ''}${collectedSummary.serviceFeeAmount > 0 ? ` Includes ${fmt(collectedSummary.serviceFeeAmount)} card service fee.` : ''}`
                          : `The required deposit of ${fmt(Number(estimate.depositAmount))} has been satisfied.`
                        : isDepositDisputedAfterRedirect
                          ? 'This deposit payment was disputed. The deposit stays outstanding until the dispute is resolved.'
                          : isDepositFailedAfterRedirect
                            ? 'The deposit payment didn’t go through. You can try again.'
                            : depositClearing
                              ? estimate.depositClearingRail === 'BANK'
                                ? 'Your bank payment was received. It usually settles in 3–5 business days, and nothing more is needed from you.'
                                : 'Your payment was received and is being confirmed. Nothing more is needed from you.'
                              : 'Payment received. We’re updating your estimate now, which can take a few seconds.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {depositSearchState === 'cancel' && (
              <div className="mb-5 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-700 shadow-sm">
                <p className="font-semibold">Deposit checkout canceled</p>
                <p className="mt-0.5 text-sm">
                  No payment was taken. You can pay the deposit whenever you are ready.
                </p>
              </div>
            )}

            {/* Stacked, the title and the action row sat a full gap-4 apart and
                read as two unrelated blocks. Tighter when stacked, unchanged
                once they sit on one line. */}
            <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Signed estimate
                </p>
                <h2 className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-[#24313A]">
                  {documentCompany?.name ?? 'Your company'}
                </h2>
                {v.estimateNumber && estimate.estimateNumber && (
                  <p className="mt-1 text-sm text-slate-500">Estimate #{estimate.estimateNumber}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Both sit on a shared 36px height so the pill and the button
                    share a baseline instead of floating apart. */}
                <span className="inline-flex h-9 items-center rounded-full bg-emerald-100 px-3 text-sm font-semibold text-emerald-700">
                  Approved
                </span>
                <a
                  href={pdfDisplayUrl}
                  download={`${pdfBaseName}-signed.pdf`}
                  className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#26343D] px-3.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#1d2830]"
                >
                  <Download size={14} />
                  Download
                </a>
              </div>
            </div>

            {/* Document first, deposit panel second — at EVERY width. Side by
                side at lg (document left, panel right); stacked below that, the
                customer reads the estimate before being asked to pay. `order`
                is unprefixed on both children so the two stay in step; the
                aside remains first in source only to keep this block readable. */}
            <div
              className={
                showApprovedAside
                  ? 'grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)] lg:items-start'
                  : ''
              }
            >
              {showApprovedAside ? (
                <aside className="order-2 mx-auto w-full max-w-xl space-y-4 lg:mx-0 lg:max-w-none lg:sticky lg:top-8">
                  {/* Keeps its own guard now that the aside can be here for
                      financing alone — otherwise a no-deposit estimate would
                      render a "$0.00 / Required" panel. */}
                  {showApprovedDeposit ? (
                    <div className="overflow-hidden rounded-3xl bg-white shadow-[0_1px_3px_rgba(36,49,58,0.06)] ring-1 ring-black/5">
                      <div className="border-b border-slate-100 bg-[#FBFAF8] px-6 pb-5 pt-5">
                        <div className="flex items-center justify-between gap-4">
                          <p className="text-sm font-semibold text-[#24313A]">
                            {depositCollected
                              ? collectedSummary
                                ? 'Deposit received'
                                : 'Deposit covered'
                              : depositClearing
                                ? 'Deposit pending'
                                : 'Deposit due'}
                          </p>
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              depositCollected
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {depositBadgeLabel({
                              depositCollected,
                              depositClearing,
                              depositClearingRail: estimate.depositClearingRail,
                              depositRequired: estimate.depositRequired,
                            })}
                          </span>
                        </div>
                        <p className="mt-4 text-4xl font-semibold tracking-[-0.04em] text-[#24313A] tabular-nums">
                          {fmt(
                            depositCollected && collectedSummary
                              ? collectedSummary.chargedAmount
                              : Number(estimate.depositAmount),
                          )}
                        </p>
                        {depositCollected && collectedDiscount > 0 ? (
                          <p className="mt-2 text-sm text-slate-500">
                            {fmt(collectedDiscount)} ACH/check discount ·{' '}
                            {fmt(collectedSummary?.coveredAmount ?? Number(estimate.depositAmount))}{' '}
                            deposit covered
                          </p>
                        ) : null}
                        <p className="mt-2 text-sm leading-5 text-slate-500">
                          {depositCollected
                            ? `Paid ${estimate.depositCollectedAt ? `on ${fmtDate(estimate.depositCollectedAt, timezone)}` : ''}`.trim()
                            : depositClearing
                              ? depositByCheck
                                ? 'Paid by check.'
                                : estimate.depositClearingRail === 'BANK'
                                  ? 'Paid by bank transfer.'
                                  : 'Payment received, being confirmed.'
                              : offersTwoDepositMethods(depositSurcharge)
                                ? 'Choose how you would like to pay to secure your scheduled work.'
                                : 'Pay the deposit to secure your scheduled work.'}
                        </p>
                      </div>

                      {/* Same 24px inset as the header so the payment rows line up
                        with the label and amount above them. */}
                      <div className="space-y-4 px-6 py-5">
                        {canPayDeposit || depositClearing ? (
                          <DepositSlot variant="approvedAside" />
                        ) : (
                          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                            <div className="flex items-center gap-2 font-semibold">
                              <CheckCircle2 className="h-4 w-4" />
                              No deposit action needed
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* Approve-then-pay means the fee is only ever charged AFTER
                      this branch takes over, so the quote body's copy of the
                      note is unreachable in that state — and the stored PDF was
                      signed before the deposit existed. Sits beside the deposit
                      panel rather than inside it: the panel is gated on the
                      CURRENT deposit amount, and the fee is history. */}
                  {showServiceFeeNote ? (
                    <ServiceFeeNote
                      serviceFee={serviceFee}
                      className="text-center text-xs text-slate-500"
                    />
                  ) : null}

                  {wisetack ? (
                    <div className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-black/5">
                      <WisetackEstimateCta wisetack={wisetack} />
                    </div>
                  ) : null}
                </aside>
              ) : null}

              {/* Padding drops to 0 when stacked: PdfViewer scales each page to
                  the container width, so every pixel of inset directly shrinks
                  the rendered text on a phone. */}
              {/* Deliberately not `overflow-hidden`: that makes this a scroll
                  container, which captures PdfViewer's sticky page toolbar and
                  carries it off screen as the document scrolls. The viewer
                  rounds its own corners, so there is nothing to clip here. */}
              <section className="order-1 min-w-0 rounded-3xl bg-white p-0 shadow-[0_1px_3px_rgba(36,49,58,0.06)] ring-1 ring-black/5 sm:p-2">
                <PdfViewer
                  url={pdfDisplayUrl}
                  className="w-full rounded-2xl bg-[#3E4548] pb-2 pt-2 sm:pb-4 sm:pt-4"
                />
              </section>
            </div>

            <PoweredByNotifi className="mt-6 flex justify-center" />
          </div>
        </div>
      </ScrollableView>
    );
  }

  // The single-scope design's brand (accent/primary/fonts), or null when the
  // presentation comes from the tenant flag / a non-single-scope design — then
  // the single-scope surfaces keep their fixed palette.
  const ssBrand =
    (resolvedDocLayout?.branding as { presentation?: string } | undefined)?.presentation ===
    'SINGLE_SCOPE'
      ? resolvedDocLayout?.branding
      : null;

  // The single-scope identity the customer sees — the DESIGN branding (logo, name,
  // tagline, license, contact) with the tenant company as a per-field fallback,
  // resolved once on the quote action context (`documentCompany`) and reused by the
  // rail, footer, Ask link and consents so every surface names one business. Only a
  // single-scope design overrides identity; every other presentation keeps the tenant.
  // The PRINTED letterhead (rail + footer) additionally drops the contact fields the
  // design hides; the Ask link and the offline-approval note keep the full identity
  // so the customer can always reach the business to answer.
  const railCompany = printableSingleScopeCompany(documentCompany, resolvedDocLayout);
  // The estimate number is a design-gated field: derived ONCE so the letterhead,
  // the hero, the footer note and the approve/decline dialogs all agree.
  const visibleEstimateNumber =
    v.estimateNumber && estimate.estimateNumber ? estimate.estimateNumber : null;

  // Shared approval flow for every public estimate presentation. Single-scope
  // opens it from the floating pill; itemized (legacy + designed documents)
  // opens it from the action rail. The legal/payment behavior stays identical.
  const approveModal = <QuoteApproveDialog />;

  // Single-scope only: the decline flow in a modal opened from the document
  // footer's "Decline this estimate" link, so it isn't a mid-page form.
  const declineModal = (
    <Dialog
      open={declineModalOpen}
      onOpenChange={(open) => {
        setDeclineModalOpen(open);
        if (!open) {
          setDeclineReason('');
          setDeclineChoice(null);
        }
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="gap-0 p-0 sm:max-w-[440px]"
        style={singleScopeThemeVars(ssBrand)}
      >
        <div className="flex items-center gap-3 border-b border-[#14141A]/[0.08] px-5 py-[18px]">
          <DialogTitle className="min-w-0 flex-1 text-[17px] font-medium tracking-[-0.02em] text-[#17171A]">
            Decline estimate{visibleEstimateNumber ? ` ${visibleEstimateNumber}` : ''}
          </DialogTitle>
          <button
            type="button"
            onClick={() => setDeclineModalOpen(false)}
            aria-label="Close"
            className="flex size-9 shrink-0 items-center justify-center rounded-md border border-[#14141A]/[0.13] text-[#63646B] transition-colors hover:border-[#14141A]/[0.30] hover:text-[#17171A]"
          >
            <X className="size-[17px]" strokeWidth={1.8} />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <p className="text-[13.5px] leading-[1.5] text-[#63646B]">
            Tell us why so we can do better. This does not cancel anything else on your account.
          </p>
          <div className="space-y-2.5">
            {[
              'Price is too high',
              'Getting another quote',
              'Timing is wrong',
              'Going a different direction',
              'Other',
            ].map((reason) => {
              const selected = declineChoice === reason;
              return (
                <button
                  key={reason}
                  type="button"
                  onClick={() => {
                    setDeclineChoice(reason);
                    // Preset reasons ARE the submitted reason; 'Other' clears it
                    // so the free-text field owns what gets sent.
                    setDeclineReason(reason === 'Other' ? '' : reason);
                  }}
                  className={`flex w-full items-center gap-3 rounded-[10px] border px-4 py-3.5 text-left transition-colors ${
                    selected
                      ? 'border-[var(--ss-accent)]'
                      : 'border-[#14141A]/[0.13] hover:bg-[#14141A]/[0.02]'
                  }`}
                >
                  <span
                    className={`flex size-5 shrink-0 items-center justify-center rounded-full border ${
                      selected
                        ? 'border-[#17171A] bg-[#17171A] text-white'
                        : 'border-[#95969D] bg-white'
                    }`}
                  >
                    {selected ? <Check className="size-3" strokeWidth={3} /> : null}
                  </span>
                  <span className="text-[15px] text-[#17171A]">{reason}</span>
                </button>
              );
            })}
          </div>
          {declineChoice === 'Other' && (
            <textarea
              autoFocus
              className="w-full rounded-[10px] border border-[#14141A]/[0.13] px-3 py-2 text-[14px] text-[#17171A] outline-none focus:border-[#63646B]"
              rows={3}
              placeholder="Tell us more…"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              maxLength={2000}
            />
          )}
        </div>
        <div className="flex gap-2.5 border-t border-[#14141A]/[0.08] bg-[#FBFBFA] px-5 py-[15px]">
          <Button
            variant="outline"
            className="h-11 flex-1 text-[15px] font-medium"
            onClick={() => setDeclineModalOpen(false)}
            disabled={submitting}
          >
            Keep it open
          </Button>
          <Button
            className="h-11 flex-1 bg-[#17171A] text-[15px] font-medium text-white hover:bg-[#33343A]"
            onClick={handleDecline}
            disabled={submitting || !declineChoice}
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Decline
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // The deposit + approve/decline/signature panel. Hoisted so the two
  // presentations can PLACE it differently without duplicating the flow:
  // ITEMIZED keeps it as a sticky right rail; SINGLE_SCOPE flows it inline at the
  // bottom of the single document column (artifact parity — no side rail).
  const actionPanel = (
    <div
      className={
        presentation === 'SINGLE_SCOPE'
          ? // Single-scope flows flat: Approve is the pill, Decline is a quiet
            // link, so there is no boxed "decision card" — only the deposit CTA
            // (post-approval) carries its own frame.
            ''
          : 'rounded-lg bg-white p-6 shadow-sm'
      }
    >
      {/* Status chips for terminal states */}
      {isApprovedPresentation && (
        <div className="mb-4 flex justify-center">
          <span className="inline-flex items-center rounded-full bg-green-100 px-4 py-2 text-sm font-semibold text-green-700">
            Approved
          </span>
        </div>
      )}
      {effectiveStatus === 'DECLINED' && (
        <div className="mb-4 flex justify-center">
          <span className="inline-flex items-center rounded-full bg-red-100 px-4 py-2 text-sm font-semibold text-red-700">
            Declined
          </span>
        </div>
      )}
      {effectiveStatus === 'EXPIRED' && (
        <div className="mb-4 flex justify-center">
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-4 py-2 text-sm font-semibold text-yellow-700">
            Expired
          </span>
        </div>
      )}

      {/* Summary — omitted on single-scope, where the document's own
                    "Your total" card already carries the breakdown. */}
      {presentation !== 'SINGLE_SCOPE' && (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Subtotal</span>
            <span className="text-[#2B373F]">{fmt(subtotalWithoutSetupFee)}</span>
          </div>
          {displayMembershipSavings > 0 && (
            <div className="flex justify-between">
              <span className="text-emerald-600">
                Membership savings
                {membershipSavingsPlanNames ? ` (${membershipSavingsPlanNames})` : ''}
              </span>
              <span className="text-emerald-600">-{fmt(displayMembershipSavings)}</span>
            </div>
          )}
          {membershipPlanPrice > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">{membershipOffer?.planName ?? 'Membership'}</span>
              <span className="text-[#2B373F]">{fmt(membershipPlanPrice)}</span>
            </div>
          )}
          {membershipSetupFee > 0 && (
            <div className="flex justify-between">
              <span>One-time setup fee</span>
              <span>{fmt(membershipSetupFee)}</span>
            </div>
          )}
          {selectedDiscountTotal > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">{estimateDiscountLabel}</span>
              <span className="text-[#2B373F]">-{fmt(selectedDiscountTotal)}</span>
            </div>
          )}
          {!acceptMembershipOffer && taxBreakdown.length > 0 ? (
            taxBreakdown.map((row, index) => (
              <div
                key={`${row.primary}-${row.secondary ?? ''}-${row.jurisdictionType ?? ''}-${row.rate}-${index}`}
                className="flex items-start justify-between gap-3"
              >
                <span className="flex min-w-0 flex-col text-gray-500">
                  <span>{taxBreakdownDisplayName(row)}</span>
                  <span className="text-[11px] text-gray-400">{taxBreakdownDisplayMeta(row)}</span>
                  {row.registrationNumber ? (
                    <span className="font-mono text-[10px] text-gray-400">
                      Reg #: {row.registrationNumber}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-[#2B373F]">{fmt(row.amount)}</span>
              </div>
            ))
          ) : (
            <div className="flex justify-between">
              <span className="text-gray-500">Tax</span>
              <span className="text-[#2B373F]">{fmt(approvalTaxTotal)}</span>
            </div>
          )}
          <p className="text-[11px] text-gray-500">
            Final taxes may be recalculated when invoiced.
          </p>
          <div className="h-px bg-gray-200" />
          <div className="flex justify-between font-bold text-[#2B373F]">
            <span>Total</span>
            <span>{fmt(approvalGrandTotal)}</span>
          </div>
          {selectedDeposit > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Deposit</span>
              <span className="font-medium text-[#2B373F]">{fmt(selectedDeposit)}</span>
            </div>
          )}
        </div>
      )}

      {presentation === 'SINGLE_SCOPE' && depositCollected && collectedSummary ? (
        <div className="mt-4 space-y-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <div className="flex justify-between gap-4 font-medium">
            <span>Deposit received</span>
            <span>{fmt(collectedSummary.chargedAmount)}</span>
          </div>
          {collectedDiscount > 0 ? (
            <div className="flex justify-between gap-4">
              <span>ACH/check discount</span>
              <span>{fmt(collectedDiscount)}</span>
            </div>
          ) : null}
          {collectedSummary.coveredAmount != null ? (
            <div className="flex justify-between gap-4">
              <span>Deposit covered</span>
              <span>{fmt(collectedSummary.coveredAmount)}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {depositSearchState === 'success' && (
        <div
          className={`mt-4 rounded-lg border px-3 py-2 text-sm ${
            depositCollected
              ? 'border-green-200 bg-green-50 text-green-800'
              : isDepositDisputedAfterRedirect
                ? 'border-orange-200 bg-orange-50 text-orange-900'
                : isDepositFailedAfterRedirect
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          {depositCollected
            ? 'Deposit paid successfully.'
            : isDepositDisputedAfterRedirect
              ? 'This deposit payment was disputed.'
              : isDepositFailedAfterRedirect
                ? 'The deposit payment didn’t go through.'
                : 'Payment received. Finishing up now.'}
        </div>
      )}

      {depositSearchState === 'cancel' && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          Deposit checkout canceled. No payment was taken.
        </div>
      )}

      {/* Financing — itemized keeps it inside this panel; single-scope renders
          it as its own card in the document flow (see the layout below). */}
      {presentation !== 'SINGLE_SCOPE' && wisetack && showDecisionFinancing ? (
        <div className="mt-6 border-t pt-6">
          <WisetackEstimateCta wisetack={wisetack} />
        </div>
      ) : null}

      {/* Download button — single-scope already carries download in the top
          letterhead, so it isn't repeated here; itemized keeps it in-panel. */}
      {presentation !== 'SINGLE_SCOPE' && (
        <button
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-[#374151] transition-colors hover:bg-gray-50 disabled:opacity-50"
          onClick={handleDownloadPdf}
        >
          <Download size={15} />
          Download Estimate
        </button>
      )}

      {/* Action section (ITEMIZED only). Approve opens the shared modal; Decline
                    keeps its existing compact rail flow. Single-scope owns both
                    triggers elsewhere, so it renders nothing here. */}
      {canApproveOnPage && presentation !== 'SINGLE_SCOPE' && <ApproveDeclineSlot />}
      {/* Flattened from a nested card to a divided section: the inner
                    card's own padding squeezed the payment rows until their
                    labels wrapped. The sidebar card is the surface. */}
      {(canPayDeposit || depositClearing) && (
        <div
          className={
            presentation === 'SINGLE_SCOPE'
              ? 'mt-6 space-y-4 rounded-[10px] border border-[#14141A]/[0.13] bg-white p-6'
              : 'mt-6 space-y-4 border-t pt-6'
          }
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Deposit payment
            </p>
            <div className="mt-2 flex items-end justify-between gap-4">
              <p className="text-sm font-medium leading-5 text-[#2B373F]">
                {depositClearing
                  ? depositByCheck
                    ? 'Deposit paid by check'
                    : estimate.depositClearingRail === 'BANK'
                      ? 'Deposit paid by bank transfer'
                      : 'Deposit payment received'
                  : 'Pay to get scheduled'}
              </p>
              <p className="text-xl font-semibold tracking-[-0.03em] text-[#24313A]">
                {fmt(depositChargeAmount)}
              </p>
            </div>
          </div>
          <DepositSlot variant="sidebar" />
          {/* P5 intended financing to read as a first-class deposit
                        option here, but this card ALREADY renders the same CTA
                        further down (it is not gated on canPayDeposit), so an
                        approved estimate with a deposit due showed the whole
                        financing section — heading, button and the full Wisetack
                        legal disclosure — twice in one column. The lower site is
                        the one that survives every state, so this copy goes. */}
        </div>
      )}
      {canAct && offlineNote && (
        <div className={presentation === 'SINGLE_SCOPE' ? 'mt-6' : 'mt-6 border-t pt-6'}>
          {/* This estimate carries no Approve button by the sender's
                        choice, so this line is the customer's only route to
                        answering — it names and links the channel rather than
                        telling them to "contact us" with nothing to contact. */}
          <p className="text-center text-sm break-words text-[#374151]">
            {offlineNote.lead}
            {offlineNote.link && (
              <>
                <a
                  href={offlineNote.link.href}
                  className="font-medium underline underline-offset-2"
                >
                  {offlineNote.link.label}
                </a>
                {offlineNote.tail}
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );

  // The single-scope document column — hero, scope card, totals, financing,
  // reviews, footer. Rendered only in the single-scope layout below (the
  // itemized default has its own ItemizedEstimateDocument).
  const documentBody = (
    <>
      {/* Hero — one headline price, status, and the facts a customer checks
          first. Additive above the document proper. */}
      <div className="mb-6">
        <SingleScopeHero
          estimateNumber={
            v.estimateNumber && estimate.estimateNumber ? estimate.estimateNumber : undefined
          }
          title={customerVisibleTitle || 'Your estimate'}
          preparedFor={
            estimate.customer?.name ? (
              <>
                {estimate.customer.name}
                {estimate.customer.address?.trim() ? (
                  <>
                    <br />
                    <span className="font-normal text-[#63646B]">
                      {estimate.customer.address.trim()}
                    </span>
                  </>
                ) : null}
                {v.customerPhone && estimate.customer.phone?.trim() ? (
                  <>
                    <br />
                    <span className="font-normal text-[#63646B]">
                      {formatPhoneDisplay(estimate.customer.phone)}
                    </span>
                  </>
                ) : null}
              </>
            ) : undefined
          }
          statusPill={(() => {
            const label = isApprovedPresentation
              ? 'Approved'
              : effectiveStatus === 'DECLINED'
                ? 'Declined'
                : effectiveStatus === 'EXPIRED' || isExpiredByDate
                  ? 'Expired'
                  : canAct
                    ? estimate.signatureRequired
                      ? 'Ready to sign'
                      : 'Ready for approval'
                    : 'Estimate';
            return (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E4682F]/[0.34] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-[#17171A]">
                {/* The artifact's status dot is always ink, not status-colored. */}
                <span className="size-1.5 rounded-full bg-[#17171A]" />
                {label}
              </span>
            );
          })()}
          validNote={(() => {
            if (!v.validUntil || !estimate.validUntil || !validUntilDate) return null;
            const today = dateInTimeZone(new Date().toISOString(), timezone);
            if (!today) return null;
            const days = Math.round((validUntilDate.getTime() - today.getTime()) / 86_400_000);
            return days > 0 ? `${days} ${days === 1 ? 'day' : 'days'} left` : null;
          })()}
          facts={(
            [
              v.serviceAddress && serviceAddress
                ? { label: 'Service address', value: serviceAddress }
                : null,
              v.date
                ? { label: 'Date created', value: fmtDate(estimate.createdAt, timezone) }
                : null,
              v.validUntil && estimate.validUntil
                ? { label: 'Valid until', value: fmtDateOnly(estimate.validUntil) }
                : null,
            ] as Array<{ label: string; value: ReactNode } | null>
          ).filter((f): f is { label: string; value: ReactNode } => f !== null)}
        />
      </div>
      {/* On single-scope this outer wrapper is transparent — the sections flow
          flat on the white surface (each carries its own frame where needed),
          matching the artifact; itemized keeps the boxed white document card. */}
      {/* Single-scope flows flat on the white surface — each section carries
          its own frame where needed — so this wrapper stays transparent. The
          itemized document is rendered separately by ItemizedEstimateDocument. */}
      <div className="">
        {/* What's included — the scope's deliverables, folded into one scope
            price with a plain ✓ checklist (no per-line money). */}
        {
          <div className="mt-6 overflow-hidden rounded-[10px] border border-[#14141A]/[0.13] bg-white">
            {/* Scope card — the artifact's .solo: a two-column body pairing the
                scope write-up + "what's included" checklist (left) with a tinted
                price rail (right) that carries the headline scope price. */}
            <div className="flex flex-col lg:flex-row">
              <div className="min-w-0 flex-1 px-[26px] py-6">
                {estimate.title ? (
                  <h4 className="text-[19px] font-medium leading-[1.08] tracking-[-0.02em] text-[#17171A]">
                    {estimate.title}
                  </h4>
                ) : null}
                {hasReadableContent(estimate.scopeOfWork) ? (
                  isHtmlContent(estimate.scopeOfWork) ? (
                    <div
                      className={`mt-2 max-w-none text-[14.5px] leading-[1.6] text-[#63646B] ${RICH_TEXT_CLASS}`}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(estimate.scopeOfWork),
                      }}
                    />
                  ) : (
                    <p className="mt-2 whitespace-pre-wrap text-[14.5px] leading-[1.6] text-[#63646B]">
                      {estimate.scopeOfWork}
                    </p>
                  )
                ) : null}
                <p className="mb-3 mt-[22px] text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#95969D]">
                  What is included
                </p>
                <ul className="divide-y divide-[#14141A]/[0.06]">
                  {requiredRows.map((row, i) => {
                    const item = row.line;
                    const detail = lineDetailVisibility(item.displayConfig);
                    return (
                      <li key={`${item.lineItemId}-${i}`} className="flex items-start gap-3 py-3">
                        <Check
                          className="mt-0.5 size-4 shrink-0 text-[#E4682F]"
                          strokeWidth={2.25}
                        />
                        {v.itemImages ? <LineItemPhoto line={item} /> : null}
                        <div className="min-w-0">
                          <p className="text-[14.5px] font-normal tracking-[-0.006em] text-[#33343A]">
                            {item.name}
                          </p>
                          {v.itemDescriptions && detail.description && item.description ? (
                            <RichTextContent
                              content={item.description}
                              className="mt-0.5 text-[13px] leading-[1.5] text-[#63646B]"
                            />
                          ) : null}
                          {detail.included && row.includedNames.length > 0 ? (
                            <ul className="mt-1.5 space-y-1 border-l border-[#14141A]/[0.08] pl-3">
                              {row.includedNames.map((child, idx) => (
                                <li
                                  key={`${child.name}-${idx}`}
                                  className="text-[12px] text-[#95969D]"
                                >
                                  {child.name}
                                  {v.quantities && child.quantity > 1 ? ` × ${child.quantity}` : ''}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {/* Customer note — folded into the scope card on single-scope,
                    since this surface drops the standalone blue "Message" box. */}
                {v.notes && hasReadableContent(customerNotes) ? (
                  <div className="mt-5 border-t border-[#14141A]/[0.06] pt-4">
                    {isHtmlContent(customerNotes) ? (
                      <div
                        className={`max-w-none text-[13px] leading-[1.55] text-[#63646B] ${RICH_TEXT_CLASS}`}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(customerNotes) }}
                      />
                    ) : (
                      <p className="whitespace-pre-wrap text-[13px] leading-[1.55] text-[#63646B]">
                        {customerNotes}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Price rail — tinted, divided; the scope's headline price. In a
                  single-scope quote the scope price IS the grand total, so this
                  tracks the live figure the customer will approve. */}
              <div className="flex shrink-0 flex-col border-t border-[#14141A]/[0.13] bg-[#FBFBFA] p-6 lg:w-[282px] lg:border-l lg:border-t-0">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[#95969D]">
                  Your price
                </p>
                <p className="mt-2.5 text-[38px] font-medium leading-none tracking-[-0.038em] tabular-nums text-[#17171A]">
                  {fmt(approvalGrandTotal)}
                </p>
                <p className="mt-2 text-[12.5px] leading-[1.5] text-[#63646B]">
                  Includes materials, labor, and applicable tax.
                </p>
              </div>
            </div>

            {/* Full-breakdown footer strip — tinted, with a disclosure chevron. */}
            {requiredRows.length > 0 ? (
              <details className="group border-t border-[#14141A]/[0.13] bg-[#FBFBFA] px-[26px] py-3.5">
                <summary className="flex cursor-pointer items-center gap-1.5 text-[13px] font-medium text-[#33343A] hover:text-[#17171A]">
                  <ChevronRight className="size-3.5 shrink-0 transition-transform group-open:rotate-90" />
                  Full breakdown
                </summary>
                {/* The artifact's breakdown is a priced table — name (+ qty) on
                    the left, the line's own total on the right — so a customer who
                    opens it sees exactly how the one scope price is built up. */}
                <table className="mt-3 w-full text-[12.5px] text-[#63646B]">
                  <tbody>
                    {requiredRows.map((row, i) => {
                      const originalTotal = row.lines
                        .map(resolveEstimateLineAmounts)
                        .reduce((sum, amounts) => sum + amounts.lineTotal, 0);
                      const memberAdjustment = acceptMembershipOffer
                        ? membershipOfferAdjustmentForLines(
                            membershipOffer,
                            row.lines,
                            estimate.lineItems,
                          )
                        : null;
                      return (
                        <tr key={`fb-${row.line.lineItemId}-${i}`} className="align-top">
                          <td className="py-1 pr-3">
                            {row.line.name}
                            {v.quantities && row.line.quantity > 1 ? (
                              <span className="text-[#95969D]"> × {row.line.quantity}</span>
                            ) : null}
                            {memberAdjustment ? (
                              <span className="ml-2 text-[10px] font-medium text-emerald-700">
                                Member price · save {fmt(memberAdjustment.savings)}
                              </span>
                            ) : null}
                          </td>
                          <td className="py-1 text-right tabular-nums text-[#33343A]">
                            {memberAdjustment ? (
                              <span className="flex flex-col items-end">
                                <span className="text-[#95969D] line-through">
                                  {fmt(originalTotal)}
                                </span>
                                <span className="font-medium text-[#17171A]">
                                  {fmt(memberAdjustment.memberTotal)}
                                </span>
                              </span>
                            ) : (
                              fmt(originalTotal)
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </details>
            ) : null}
          </div>
        }

        {/* Optional add-ons — shared with the portal estimate surface. */}
        <OptionalSelectionSlot />

        {/* Membership follows the recommendations, before the final total. */}
        {membershipOffer ? (
          <div className="mt-6">
            <MembershipOfferBlock
              offer={membershipOffer}
              selected={acceptMembershipOffer}
              onSelectedChange={setAcceptMembershipOffer}
              selectedBillingOptionId={selectedMembershipBillingOptionId}
              onBillingOptionChange={setSelectedMembershipBillingOptionId}
              variant="banner"
              className="w-full"
            />
          </div>
        ) : null}

        {/* Your total — single-scope prints the heading as a plain h3 OUTSIDE
            the card (artifact) and folds the scope subtotal into a descriptive
            "Scope of work — {title}" line; itemized keeps the boxed panel. */}
        <h3 className="mt-8 text-[22px] font-normal tracking-[-0.026em] text-[#17171A]">
          Your total
        </h3>
        <div className="mt-3 rounded-[10px] border border-[#14141A]/[0.08] px-[21px] py-[19px]">
          <div className="space-y-2.5 text-[14.5px]">
            {/* Scope row is the required-only subtotal; each ticked add-on is
                its own line, so no figure is labeled with more than it covers.
                Muted label / ink value, matching the artifact's .kv. */}
            <div className="flex justify-between gap-3">
              <span className="min-w-0 text-[#63646B]">
                {`Scope of work${estimate.title ? ` — ${estimate.title}` : ''}`}
              </span>
              <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                {fmt(requiredSubtotal)}
              </span>
            </div>
            {displayedOptionalRows.map((row, i) => (
              <div key={`addon-${i}`} className="flex justify-between gap-3">
                <span className="min-w-0 text-[#63646B]">{row.name}</span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  {fmt(row.subtotal)}
                </span>
              </div>
            ))}

            {displayMembershipSavings > 0 && (
              <div className="flex justify-between">
                <span className="text-emerald-600">
                  Membership savings
                  {membershipSavingsPlanNames ? ` (${membershipSavingsPlanNames})` : ''}
                </span>
                <span className="text-emerald-600">-{fmt(displayMembershipSavings)}</span>
              </div>
            )}

            {membershipPlanPrice > 0 && (
              <div className="flex justify-between">
                <span className="text-[#63646B]">{membershipOffer?.planName ?? 'Membership'}</span>
                <span className="text-[#17171A]">{fmt(membershipPlanPrice)}</span>
              </div>
            )}

            {membershipSetupFee > 0 && (
              <div className="flex justify-between">
                <span>One-time setup fee</span>
                <span>{fmt(membershipSetupFee)}</span>
              </div>
            )}
            {selectedDiscountTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-[#374151]">{estimateDiscountLabel}</span>
                <span className="text-[#374151]">-{fmt(selectedDiscountTotal)}</span>
              </div>
            )}

            {/* Per-tax-code lines (v60 named jurisdictions) */}
            {!acceptMembershipOffer && taxBreakdown.length > 0 ? (
              taxBreakdown.map((tb, i) => {
                const meta = taxBreakdownDisplayMeta(tb);
                return (
                  <div
                    key={`${tb.primary}-${tb.secondary ?? ''}-${tb.jurisdictionType ?? ''}-${tb.rate}-${i}`}
                    className="flex items-start justify-between gap-3"
                  >
                    <div className="flex min-w-0 flex-col">
                      {/* The rate/jurisdiction is folded into the label as a
                          parenthetical ("Sales tax (8.25% …)"), matching the
                          artifact. */}
                      <span className="text-[#374151]">
                        {taxBreakdownDisplayName(tb)}
                        {meta ? <span className="text-[#95969D]"> ({meta})</span> : null}
                      </span>
                      {/* NOT-7 — registration number rendered under the tax label */}
                      {tb.registrationNumber ? (
                        <span className="font-mono text-[10px] text-[#6B7280]">
                          Reg #: {tb.registrationNumber}
                        </span>
                      ) : null}
                    </div>
                    <span className="shrink-0 tabular-nums text-[#374151]">{fmt(tb.amount)}</span>
                  </div>
                );
              })
            ) : (
              <div className="flex justify-between gap-3">
                <span className="text-[#63646B]">Sales tax</span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  {fmt(approvalTaxTotal)}
                </span>
              </div>
            )}

            <div className="mt-[13px] h-px bg-[#14141A]/[0.13]" />
            <div className="flex items-baseline justify-between gap-3 pt-[15px]">
              <span className="text-[15px] font-medium text-[#17171A]">Total due</span>
              <span className="text-[29px] font-medium leading-none tracking-[-0.032em] tabular-nums text-[#17171A]">
                {fmt(approvalGrandTotal)}
              </span>
            </div>

            {/* P4 — record of the card service fee that was charged on
                        the deposit. Only reaches here once a card deposit was
                        collected, so it is stated in the past tense and never
                        added to the total. Shares the approved view's guard so
                        the two sites can never disagree about the fee. */}
            {showServiceFeeNote ? <ServiceFeeNote serviceFee={serviceFee} /> : null}
          </div>
        </div>

        {/* Financing — its own card, always right after the total: the customer
            has just read what they owe, and this is where they can spread it.
            Kept out of the decision actions and ahead of the fine print. */}
        {wisetack && showDecisionFinancing ? (
          <div className="mt-8 rounded-[10px] border border-[#14141A]/[0.13] bg-white p-6">
            <WisetackEstimateCta wisetack={wisetack} />
          </div>
        ) : null}

        {/* The artifact's .dsec--who — reduced to just the job number. "Prepared
            for" (the customer) and the Issued / Valid until dates live in the
            hero, and the preparer name is intentionally not shown, so this
            renders only when the estimate references a job. */}
        {estimate.jobReference?.jobNumber && (
          <dl className="mt-8 grid grid-cols-[auto_1fr] gap-x-5 gap-y-[9px] text-[14.5px]">
            <dt className="text-[#95969D]">Job</dt>
            <dd className="m-0 text-right font-medium text-[#17171A]">
              {estimate.jobReference.jobNumber}
            </dd>
          </dl>
        )}

        {/* Payment plan — visible in the quote body so the customer
                    sees the schedule they're agreeing to, not only inside the
                    PDF/approval flow (P2). Mirrors EstimatePDF's payment-plan
                    block: dollar amount per stage, or "Remaining balance". */}
        {!acceptMembershipOffer && paymentPlan?.stages?.length ? (
          <div className="mt-6">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="mb-2 text-sm font-semibold text-[#2B373F]">Payment plan</p>
              {paymentPlan.previewText ? (
                <p className="mb-3 text-xs text-[#6B7280]">{paymentPlan.previewText}</p>
              ) : null}
              <div className="space-y-2">
                {/* Rows, not stages: the deposit is part of the plan and the
                    stages are resolved net of it, so listing only the stages
                    leaves the money not adding up to the Total below. */}
                {paymentPlanRows(paymentPlan).map((row) => (
                  <div key={row.key} className="flex justify-between gap-3 text-sm">
                    <span className="min-w-0 text-[#374151]">
                      <span className="font-medium text-[#2B373F]">{row.label}</span>
                      <span className="text-[#6B7280]"> · {row.detail}</span>
                    </span>
                    <span className="flex-shrink-0 text-[#2B373F]">
                      {row.amount == null
                        ? 'Remaining balance'
                        : `${fmt(row.amount)}${row.isRemaining ? ' (remaining)' : ''}`}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3 h-px bg-gray-200" />
              <div className="mt-2 flex justify-between text-sm font-semibold text-[#2B373F]">
                <span>Total</span>
                <span>{fmt(paymentPlan.grandTotal)}</span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Recurring service block — near the total (NOT-224) */}
        {estimate.bookingType === 'RECURRING' && estimate.recurringConfig
          ? (() => {
              const parts = formatRecurringParts(estimate.recurringConfig);
              return (
                <div className="mt-6 flex justify-end">
                  <div className="w-72 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <CalendarClock className="size-4 text-[#2B373F]" />
                      <h3 className="text-sm font-semibold text-[#2B373F]">Recurring service</h3>
                    </div>
                    <dl className="space-y-1.5 text-sm">
                      {parts.details.map((detail) => (
                        <div key={detail.label} className="flex justify-between gap-3">
                          <dt className="text-gray-500">{detail.label}</dt>
                          <dd className="text-right font-medium text-[#2B373F]">{detail.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>
              );
            })()
          : null}

        {/* Attachment Images — framed as "Photos from the visit", matching the
            artifact. Placed before the fine print / who sections, mirroring the
            artifact's document order. */}
        {attachmentImages.length > 0 && (
          <div className="mt-8">
            <h3 className="text-[22px] font-normal tracking-[-0.026em] text-[#17171A]">
              Photos from the visit
            </h3>
            <p className="mb-4 mt-1 text-[14.5px] leading-[1.6] text-[#63646B]">
              Tap any photo to see it full size.
            </p>
            <AttachmentImageGrid images={attachmentImages} />
          </div>
        )}

        {/* Terms & Conditions — hidden outright when there are none,
                    rather than a heading over blank space. `#estimate-terms`
                    only has to exist for `scrollToTerms()`, whose one caller is
                    the "terms above" link inside `approvalAgreement` — and that
                    link renders only when `hasTerms`, so nothing is ever left
                    pointing at a section that is no longer on the page. */}
        {hasTerms || (v.validUntil && estimate.validUntil) ? (
          <div id="estimate-terms" className="mt-8 scroll-mt-24">
            <h3 className="mb-3 text-[22px] font-normal tracking-[-0.026em] text-[#17171A]">
              The fine print
            </h3>
            {/* The artifact's .acc — collapsible rows in one bordered card.
                    The generic process/validity rows always render; the tenant's
                    real terms row only when there are terms. No invented copy. */}
            <div className="overflow-hidden rounded-[10px] border border-[#14141A]/[0.08] bg-white">
              {hasTerms ? (
                <details className="group">
                  <summary className="flex cursor-pointer items-center justify-between gap-3 px-5 py-3.5 text-[14.5px] font-medium text-[#17171A]">
                    Terms &amp; conditions
                    <ChevronRight className="size-4 shrink-0 text-[#95969D] transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-4">
                    <CustomerTermsBody
                      terms={terms}
                      className="text-[13.5px] leading-[1.62] text-[#63646B]"
                    />
                  </div>
                </details>
              ) : null}
              <details className="group border-t border-[#14141A]/[0.08] first:border-t-0">
                <summary className="flex cursor-pointer items-center justify-between gap-3 px-5 py-3.5 text-[14.5px] font-medium text-[#17171A]">
                  What happens after you approve
                  <ChevronRight className="size-4 shrink-0 text-[#95969D] transition-transform group-open:rotate-90" />
                </summary>
                <p className="px-5 pb-4 text-[13.5px] leading-[1.62] text-[#63646B]">
                  Approving confirms this scope and price. We&apos;ll reach out to schedule the
                  visit, and nothing is charged beyond any deposit shown above until the work is
                  done.
                </p>
              </details>
              {v.validUntil && estimate.validUntil ? (
                <details className="group border-t border-[#14141A]/[0.08]">
                  <summary className="flex cursor-pointer items-center justify-between gap-3 px-5 py-3.5 text-[14.5px] font-medium text-[#17171A]">
                    Price validity
                    <ChevronRight className="size-4 shrink-0 text-[#95969D] transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="px-5 pb-4 text-[13.5px] leading-[1.62] text-[#63646B]">
                    This quotation holds through {fmtDateOnly(estimate.validUntil)}. After that date
                    pricing may be re-quoted, and final taxes are recalculated when invoiced.
                  </p>
                </details>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Customer-facing custom fields */}
        {customFields.length > 0 && (
          <div className="mt-6 rounded-lg bg-[#F9FAFB] p-6">
            <h3 className="mb-3 text-sm font-semibold text-[#2B373F]">Additional Details</h3>
            <dl className="space-y-2">
              {customFields.map((cf) => (
                <div key={cf.fieldDefinitionId} className="flex gap-2 text-sm">
                  <dt className="font-medium text-[#374151]">{cf.label}:</dt>
                  <dd className="text-[#374151]">{cf.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </>
  );

  // Trust-building close — reviews, contact + account CTAs, "Powered by". This
  // renders AFTER the inline approve panel (money action first, reassurance
  // last).
  const closingSections = (
    <>
      {/* Social proof — data-gated on synced reviews. */}
      <SingleScopeReviews reviews={reviews} />

      {/* Closing sections — contact + account, data-gated. */}
      <SingleScopeFooter
        phone={railCompany?.phone}
        portalHref={embedded ? undefined : '/portal'}
        companyName={railCompany?.name}
        companyAddress={
          railCompany?.address
            ? [
                railCompany.address.street,
                [railCompany.address.city, railCompany.address.state].filter(Boolean).join(', '),
                railCompany.address.zip,
              ]
                .map((part) => part?.trim())
                .filter(Boolean)
                .join(', ')
            : null
        }
        license={railCompany?.contractorLicense}
        showSecureNotice={!embedded}
        expiresLabel={v.validUntil && estimate.validUntil ? fmtDateOnly(estimate.validUntil) : null}
        issuedNote={(() => {
          const num = visibleEstimateNumber;
          const issued =
            v.date && estimate.createdAt ? fmtDate(estimate.createdAt, timezone) : null;
          const lead = [
            num ? `Estimate ${num}` : 'Estimate',
            issued ? `issued ${issued}` : null,
            railCompany?.name ? `by ${railCompany.name}` : null,
          ]
            .filter(Boolean)
            .join(' ');
          return `${lead}. This is a quotation, not an invoice.`;
        })()}
        onDecline={canApproveOnPage ? () => setDeclineModalOpen(true) : undefined}
      />

      {/* Footer — the left rail already carries this on lg, so hide the
          duplicate there and keep it only for the stacked view. */}
      <div className="py-6 text-center lg:hidden">
        <PoweredByNotifi />
      </div>
    </>
  );

  const singleScopeActionBar = (
    <SingleScopeActionBar
      eyebrow={
        [
          customerVisibleTitle || null,
          selectedOptionalRows.length > 0
            ? `${selectedOptionalRows.length} add-on${selectedOptionalRows.length > 1 ? 's' : ''}`
            : null,
        ]
          .filter(Boolean)
          .join(' + ') || undefined
      }
      total={approvalGrandTotal}
      fmt={fmt}
      // Offer only the contact channels the document design makes visible.
      ask={{ phone: railCompany?.phone, email: railCompany?.email }}
      primaryLabel={canApproveOnPage ? (approveAndPay ? 'Approve and pay' : 'Approve') : undefined}
      primaryDisabled={expiredWhileActionable}
      onPrimary={() => {
        // The pill opens the shared approval modal (signature / consent / terms).
        setApproveModalOpen(true);
      }}
    />
  );

  return (
    <ScrollableView>
      {presentation === 'SINGLE_SCOPE' ? (
        // The artifact's `.pub`: a full-bleed flex row — the rail is its own
        // full-height column flush to the page edge, and the header + document
        // live in the RIGHT column (`.pubmain`), so the header never spans over
        // the rail. On mobile the rail collapses (its brand is carried by the
        // header, its contact by the footer, its rating by the reviews band).
        <div
          className="flex min-h-screen flex-col bg-[#F6F6F4] lg:flex-row"
          style={singleScopeThemeVars(ssBrand)}
        >
          <SingleScopeCompanyRail company={railCompany} reviews={reviews} />
          <div className="flex min-w-0 flex-1 flex-col">
            <SingleScopeLetterhead
              companyName={railCompany?.name ?? 'Your company'}
              logoUrl={railCompany?.logoDataUri ?? railCompany?.logoUrl}
              estimateNumber={visibleEstimateNumber ?? undefined}
              onPrint={() => window.print()}
              onDownload={handleDownloadPdf}
              portalHref={embedded ? undefined : '/portal'}
            />
            {/* `.pubwrap` — the document column, centered inside the main column. */}
            <div className="mx-auto w-full max-w-[948px] px-5 pb-32 pt-6 sm:px-[34px]">
              {documentBody}
              {/* Decision card — Download + Decline; Approve is the floating pill,
                  which opens the approve modal. */}
              <div id="estimate-actions" className="mt-8 scroll-mt-8">
                {actionPanel}
              </div>
              {closingSections}
            </div>
            {/* Floating action bar — inside the main column so its pill centers
                on the document, not the whole viewport. */}
            {singleScopeActionBar}
            {/* Decline modal, opened from the footer link. */}
            {declineModal}
          </div>
        </div>
      ) : (
        // Itemized is the default document for every tenant: the design
        // document, kept in its own component so the single-scope path above
        // can evolve without disturbing it. The
        // money/action layer is shared — `actionPanel` is passed straight in.
        <ItemizedEstimateDocument actionPanel={actionPanel} />
      )}
      {approveModal}
    </ScrollableView>
  );
}

// ─── Attachment Image Grid ────────────────────────────────────────────────────

// ─── Attachment Image Grid ────────────────────────────────────────────────────

function AttachmentImageGrid({ images }: { images: AttachmentImage[] }) {
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});
  // NOT-243: track per-image status so a failed fetch (CORS / network / !ok)
  // shows an error tile + download link instead of a perpetual spinner.
  const [statuses, setStatuses] = useState<Record<string, 'loading' | 'ready' | 'error'>>({});

  useEffect(() => {
    const created: string[] = [];
    let cancelled = false;

    (async () => {
      for (const img of images) {
        try {
          const res = await fetch(img.downloadUrl);
          if (cancelled) break;
          if (res.ok) {
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            created.push(url);
            if (!cancelled) {
              setBlobUrls((prev) => ({ ...prev, [img.id]: url }));
              setStatuses((prev) => ({ ...prev, [img.id]: 'ready' }));
            }
          } else if (!cancelled) {
            setStatuses((prev) => ({ ...prev, [img.id]: 'error' }));
          }
        } catch {
          if (!cancelled) setStatuses((prev) => ({ ...prev, [img.id]: 'error' }));
        }
      }
    })();

    return () => {
      cancelled = true;
      created.forEach(URL.revokeObjectURL);
    };
  }, [images]);

  return (
    // The artifact's .shots: auto-fill columns (min 146px) so photos flow to the
    // document width instead of a fixed 2/3-col grid.
    <div className="grid grid-cols-[repeat(auto-fill,minmax(146px,1fr))] gap-[11px]">
      {images.map((img) => (
        <a
          key={img.id}
          href={blobUrls[img.id] ?? img.downloadUrl}
          download={img.fileName}
          className="group block overflow-hidden rounded-md border border-[#14141A]/[0.08] bg-[#FBFBFA]"
        >
          {blobUrls[img.id] ? (
            <img
              src={blobUrls[img.id]}
              alt={img.fileName}
              className="h-32 w-full object-cover transition-opacity group-hover:opacity-90"
            />
          ) : statuses[img.id] === 'error' ? (
            <div className="flex h-32 w-full flex-col items-center justify-center gap-1 px-2 text-center">
              <FileWarning className="size-5 text-gray-400" />
              <span className="text-[11px] text-gray-400">
                Couldn&rsquo;t preview — tap to open
              </span>
            </div>
          ) : (
            <div className="flex h-32 w-full items-center justify-center">
              <Loader2 className="size-5 animate-spin text-gray-300" />
            </div>
          )}
          <p className="truncate px-2 py-1.5 text-xs text-gray-500">{img.fileName}</p>
        </a>
      ))}
    </div>
  );
}
